import clsx from "clsx";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { FetchedNote } from "@/services/api/fetchers/notes";
import { FocusableNoteCard } from "../Note/NoteCard";
import { storage } from "@/shared/utils/local-storage";
import usePrevious from "@/shared/hooks/usePrevious";
import { NoteAction } from "../../types";

interface NoteListProps {
  actions: (note: FetchedNote) => NoteAction[];
  className?: string;
  notes: FetchedNote[];
  onPress: (id: number, wasKeyboard?: boolean, ctrlKey?: boolean, longPress?: boolean) => void;
  selectedNotes?: Set<number>;
  isSelectionMode?: boolean;
}

export function NotesList({
  actions,
  className,
  notes,
  onPress,
  selectedNotes,
  isSelectionMode = false,
}: NoteListProps) {
  const [focusedIndex, setFocusedIndex] = useState(storage.getNotesNavigationState().focusedIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevNotesLength = usePrevious(notes.length);
  const memoizedActions = useMemo(() => {
    return (note: FetchedNote) => actions(note);
  }, [actions]);

  // Sort notes to show pinned ones first
  const sortedNotes = useMemo(
    () =>
      [...notes].sort((a, b) => {
        if (a.pinned === b.pinned) return 0;
        return a.pinned ? -1 : 1;
      }),
    [notes]
  );

  // Calculate grid dimensions
  const getGridDimensions = useCallback(() => {
    const cols = window.innerWidth >= 768 ? 4 : window.innerWidth >= 640 ? 3 : 2;
    const rows = Math.ceil(sortedNotes.length / cols);
    return { cols, rows };
  }, [sortedNotes.length]);

  const [gridDimensions, setGridDimensions] = useState(getGridDimensions);

  // Update grid dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      setGridDimensions(getGridDimensions());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getGridDimensions]);

  // Create virtual rows (each row contains multiple columns)
  const virtualRows = useMemo(() => {
    const { cols } = gridDimensions;
    const rows = [];
    for (let i = 0; i < sortedNotes.length; i += cols) {
      rows.push(sortedNotes.slice(i, i + cols));
    }
    return rows;
  }, [sortedNotes, gridDimensions]);

  // Set up virtualizer for rows
  const rowVirtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => {
      // Find the scrolling parent (the main element with overflow-y-auto)
      let element = containerRef.current?.parentElement;
      while (element) {
        const style = window.getComputedStyle(element);
        if (style.overflowY === "auto" || style.overflowY === "scroll") {
          return element;
        }
        element = element.parentElement;
      }
      // Fallback to document element
      return document.documentElement;
    },
    estimateSize: () => 268, // Note card height (256px) + gap-3 (12px)
    overscan: 2,
  });

  // Reset focused index when notes are filtered
  useEffect(() => {
    if (prevNotesLength && prevNotesLength !== sortedNotes.length) {
      setFocusedIndex(-1);
      storage.setNotesNavigationState({ focusedIndex: -1, wasKeyboard: false });
    }
  }, [sortedNotes.length, prevNotesLength]);

  // On mount, if focused index is different from -1, focus the note card corresponding to the focused index
  useEffect(() => {
    const navigationState = storage.getNotesNavigationState();
    if (focusedIndex !== -1 && containerRef.current && navigationState.wasKeyboard) {
      // Delay focus to ensure virtualizer has rendered after scroll restoration
      const attemptFocus = () => {
        const card = containerRef.current?.querySelector(`[data-note-index="${focusedIndex}"]`) as HTMLElement;
        if (card) {
          card.focus();
          return true;
        }
        return false;
      };

      // Try immediate focus first
      if (!attemptFocus()) {
        // If immediate focus fails, wait for virtualizer to render after scroll
        const timeoutId = setTimeout(() => {
          if (!attemptFocus()) {
            // If still not visible, scroll to the target row first
            const targetRow = Math.floor(focusedIndex / gridDimensions.cols);
            rowVirtualizer.scrollToIndex(targetRow, { align: "auto" });

            // Then try to focus after scroll
            setTimeout(() => {
              attemptFocus();
            }, 100);
          }
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [focusedIndex, gridDimensions.cols, rowVirtualizer]);

  return (
    <div ref={containerRef} className={clsx("relative", className)} tabIndex={-1}>
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const rowNotes = virtualRows[virtualRow.index];
          if (!rowNotes) return null;

          return (
            <div
              key={virtualRow.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="grid h-full grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {rowNotes.map((note, colIndex) => {
                  const noteIndex = virtualRow.index * gridDimensions.cols + colIndex;
                  return (
                    <FocusableNoteCard
                      actions={memoizedActions}
                      key={note.id}
                      note={note}
                      index={noteIndex}
                      isFocused={noteIndex === focusedIndex}
                      onFocus={setFocusedIndex}
                      onPress={(id, wasKeyboard, ctrlKey, longPress) => {
                        // Store the interaction method in navigation state
                        storage.setNotesNavigationState({
                          wasKeyboard: wasKeyboard,
                          // Reset focused index for mouse clicks to prevent focus restoration
                          focusedIndex: wasKeyboard ? focusedIndex : -1,
                        });
                        onPress(id, wasKeyboard, ctrlKey, longPress);
                      }}
                      isSelected={selectedNotes?.has(note.id) ?? false}
                      isSelectionMode={isSelectionMode}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
